import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { BrandRelationship } from '../../types';

import { normalizeBrandRelationship } from '../../utils/brand-relationship';

const LABELS: Record<BrandRelationship, string> = {
  you: 'شما',
  competitor: 'رقیب',
  known: 'شناخته‌شده',
  detected: 'شناسایی‌شده',
};

const BADGE_COLORS: Record<BrandRelationship, string> = {
  you: 'success',
  competitor: 'warning',
  known: 'primary',
  detected: 'secondary',
};

const BADGE_ICONS: Record<BrandRelationship, IconProp> = {
  you: 'user',
  competitor: 'user-friends',
  known: 'tag',
  detected: 'eye',
};

export function RelationshipBadge({
  relationship,
  solid = false,
  className,
  style,
}: {
  relationship: BrandRelationship | 'candidate';
  solid?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const normalized = normalizeBrandRelationship(relationship);
  const label = LABELS[normalized];
  const bg = BADGE_COLORS[normalized];
  const icon = BADGE_ICONS[normalized];

  const content = (
    <span className="d-inline-flex align-items-center gap-1">
      <FontAwesomeIcon icon={icon} className="fs--2" />
      <span>{label}</span>
    </span>
  );

  const useSolid = solid || normalized === 'known';

  if (useSolid) {
    return (
      <span
        className={classNames(
          'badge rounded-pill text-white fs--2 fw-normal',
          `bg-${bg}`,
          className,
        )}
        style={style}
      >
        {content}
      </span>
    );
  }

  return (
    <span className="d-inline-flex" style={style}>
      <SubtleBadge bg={bg} pill className={classNames('fs--2 fw-normal', className)}>
        {content}
      </SubtleBadge>
    </span>
  );
}

export function TrendBadge({
  trend,
  changePercentage,
}: {
  trend?: number | null;
  changePercentage?: number | null;
}) {
  const value = changePercentage ?? (trend == null ? null : trend);
  if (value == null) {
    return <span className="fs--2 text-300">—</span>;
  }
  if (value > 0) {
    return <span className="fs--2 fw-semibold text-success">↑ {value}%</span>;
  }
  if (value < 0) {
    return <span className="fs--2 fw-semibold text-danger">↓ {Math.abs(value)}%</span>;
  }
  return <span className="fs--2 fw-medium text-500">→ 0%</span>;
}

export function VisibilityBar({ score }: { score: number }) {
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: '6rem' }}>
      <div className="flex-grow-1 rounded-pill bg-200 overflow-hidden" style={{ height: '0.375rem' }}>
        <div
          className="h-100 rounded-pill bg-primary"
          style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
        />
      </div>
      <span
        className={classNames('text-end fs--2 fw-medium text-700')}
        style={{ width: '2rem', fontVariantNumeric: 'tabular-nums' }}
      >
        {score}%
      </span>
    </div>
  );
}
